import { MskScore } from "../../models/interfaces/msk-score";
import { User } from "../../models/interfaces/user";
import FirestoreCondition from "../../models/interfaces/firestore-condition";
import FirestoreOrder from "../../models/interfaces/firestore-order";
import FirestoreService from "./firestore-service";

const COLLECTION_NAME = "userLatestMskScores";

// -----------------------------------------------------------------------------------------
// #region Service Methods
// -----------------------------------------------------------------------------------------

/**
 * Add a new mskScore to the mskScores collection
 * @param {MskScore} mskScore - The mskScore that is being added to the collection
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<MskScore>} A promise to the newly added mskScore
 */
const add = async (
    mskScore: MskScore,
    currentUser: User | null = null
) => {
    return FirestoreService.add<MskScore>(COLLECTION_NAME, mskScore, currentUser);
};

/**
 * Delete a mskScore collection by the Id
 * @param {string} id - The Id of the mskScore being deleted
 */
const deleteById = async (
    id: string
) => {
    FirestoreService.deleteById(COLLECTION_NAME, id);
};

/**
 * Get all of the mskScores stored in the database
 * @returns {Promise<MskScore[]>} A promise for the collection of MskScores
 */
const getAll = async (order: FirestoreOrder[] = []) => {
    return FirestoreService.getAll<MskScore>(COLLECTION_NAME, order);
};

/**
 * Find the specific mskScore by the id
 * @param {string} id - The Id of the mskScore that we are retrieving
 * @return {Promise<MskScore>} A promise for the MskScore we are retrieving
 */
const get = async (
    id: string
) => {
    return FirestoreService.get<MskScore>(COLLECTION_NAME, id);
};

/**
 * Get all of the documents stored in the firestore database
 * @param {FirestoreCondition[]} conditions - The condition to query the collection of documents in Firestore
 * @param {FirestoreOrder[]} order
 * @param {number} limit
 * @return {Promise<MskScore>} A promise for the mskScores we are retrieving
 */
const getBy = async (
    conditions: FirestoreCondition[],
    order: FirestoreOrder[] = [],
    limit?: number
): Promise<MskScore[]> => {
    return FirestoreService.getBy<MskScore>(COLLECTION_NAME, conditions, order, limit);
};

/**
 * Save the specified mskScore in the database
 * @param {MskScore} mskScore - The mskScore that is being updated
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<MskScore>} A promise for the mskScore that is being updated
 */
const save = async (
    mskScore: MskScore,
    currentUser: User | null = null
) => {
    return FirestoreService.save<MskScore>(COLLECTION_NAME, mskScore, currentUser);
};

/**
 * Update the specified mskScore stored in the database
 * @param {MskScore} mskScore - The mskScore that is being updated
 * @param {User} currentUser - The user that is logged into the application
 * @return {Promise<MskScore>} A promise for the mskScore that is being updated
 */
const update = async (
    mskScore: MskScore,
    currentUser: User | null = null
) => {
    return FirestoreService.update<MskScore>(COLLECTION_NAME, mskScore, currentUser);
};

// #endregion Service Methods


// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

const UserLatestMskScoreService = {
    add,
    deleteById,
    getAll,
    get,
    getBy,
    save,
    update,
};

export default UserLatestMskScoreService;

// #endregion Exports
